import { DeleteOutlined, EditOutlined, EyeOutlined, FileExcelOutlined, FilterOutlined, PlusCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { App, Button, Drawer, Modal, Segmented, Space, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { ColumnsType } from "antd/es/table";
import type { EntryDraftRecord } from "../../api/draftApi";
import { bulkDeleteEntry, deleteEntry, getDailyPairDetails, getEntry, markDailyGroupPaid, permanentlyDeleteEntry, postPaged } from "../../api/entryApi";
import { BulkEntryModal } from "../../shared/bulk/BulkEntryModal";
import { confirmDeletePreview } from "../../shared/components/ConfirmDeletePreview";
import { DataGrid } from "../../shared/components/DataGrid";
import { ExcelImportModal } from "../../shared/components/ExcelImportModal";
import { FilterDrawer } from "../../shared/components/FilterDrawer";
import { PageHeaderActions } from "../../shared/components/PageHeaderActions";
import { EntryDraftList } from "../../shared/drafts/EntryDraftList";
import { getApiErrorMessage } from "../../shared/utils/apiError";
import { formatInr } from "../../shared/utils/currency";
import { formatDate } from "../../shared/utils/date";
import { hasPermission } from "../../shared/utils/permissions";
import { statementManualColumns, type DailySummaryRow, type EntryPageConfig } from "./entryConfigs";
import { DailyEntryDetailsCanvas, EntryDetailsCanvas } from "./EntryDetailsCanvas";
import { EntryUpsertDrawer } from "./EntryUpsertDrawer";
import { getFilterTags, getNumberValue, getStringValue, normalizeFilterValues } from "./entryPageUtils";
import { PendingReportModal } from "./PendingReportModal";

const detailViewColumns: ColumnsType<any> = [
  { title: "Date", dataIndex: "entryDate", width: 130, render: formatDate },
  { title: "Paid Of", dataIndex: "paidOfName", width: 220 },
  { title: "Entries", dataIndex: "entryCount", width: 90 },
  { title: "Paid", dataIndex: "paidAmount", align: "right", width: 130, render: formatInr },
  { title: "Pending", dataIndex: "pendingAmount", align: "right", width: 130, render: formatInr },
  { title: "Debit", dataIndex: "debitAmount", align: "right", width: 130, render: formatInr },
  { title: "Credit", dataIndex: "creditAmount", align: "right", width: 130, render: formatInr },
  { title: "Cumulative", dataIndex: "cumulativeAmount", align: "right", width: 150, render: formatInr }
];

const deletedDailyEntryColumns: ColumnsType<DailySummaryRow> = [
  { title: "Entry Date", width: 130, render: (_, row) => formatDate(row.paymentEntryDate ?? row.entryDate) },
  { title: "Paid Of", dataIndex: "paidOfName", width: 220 },
  { title: "Payment Of", width: 200, render: (_, row) => (row as any).paymentOfName ?? row.paymentOf ?? "-" },
  { title: "Payment", dataIndex: "paymentType", width: 110, render: (value) => <Tag>{String(value ?? "-")}</Tag> },
  { title: "Amount", dataIndex: "amount", align: "right", width: 140, render: formatInr },
  { title: "Deleted At", dataIndex: "deletedAt", width: 180, render: (value) => value ? new Date(String(value)).toLocaleString("en-IN") : "-" },
  { title: "Deleted By", dataIndex: "deletedBy", width: 190, render: (value) => value || "-" },
  { title: "Accounting", width: 120, render: () => <Tag color="red">Reversed</Tag> }
];

export function EntryPage<T extends DailySummaryRow>({ config }: { config: EntryPageConfig<T> }) {
  const { message } = App.useApp();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRows, setDetailsRows] = useState<any[]>([]);
  const [detailsRecord, setDetailsRecord] = useState<Record<string, unknown> | null>(null);
  const [detailsTitle, setDetailsTitle] = useState("Daily Entry Details");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsGroup, setDetailsGroup] = useState<{ paidOfId?: number; paymentOfId?: number; entryDate?: string; paidOfName?: string } | null>(null);
  const [upsertOpen, setUpsertOpen] = useState(false);
  const [editId, setEditId] = useState<string | number | null>(null);
  const [view, setView] = useState(config.key === "StatementEntry" ? "bank" : "summary");
  const [selectedDraft, setSelectedDraft] = useState<EntryDraftRecord | null>(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState<number[]>([]);
  const [filters, setFilters] = useState<Record<string, unknown>>({ pageNumber: 1, pageSize: 100 });

  const supportsDraftTab = config.key === "DailyEntry" || config.key === "CashEntry";
  const isDraftView = supportsDraftTab && view === "drafts";
  const isDeletedView = config.key === "DailyEntry" && view === "deleted";
  const isStatementManual = config.key === "StatementEntry" && view === "manual";
  const isStatementBank = config.key === "StatementEntry" && view !== "manual";
  const activeListUrl = isDeletedView
    ? "/daily-entries/deleted/paged"
    : config.key === "DailyEntry" && view === "details"
    ? "/daily-entries/details-view"
    : isStatementManual
      ? "/statement-entries/paged"
      : config.listUrl;
  const activeColumns = isDeletedView
    ? deletedDailyEntryColumns
    : config.key === "DailyEntry" && view === "details"
    ? detailViewColumns
    : isStatementManual
      ? statementManualColumns
      : config.columns;
  const filterTags = getFilterTags(filters);
  const canMutate = !config.readOnly || isStatementManual;
  const canDelete = canMutate && !!config.deleteUrl && hasPermission(`${config.permissionPrefix}.Delete`);
  const canCreate = canMutate && !!config.saveUrl && hasPermission(`${config.permissionPrefix}.Create`);
  const canUpdate = canMutate && !!config.saveUrl && hasPermission(`${config.permissionPrefix}.Update`);
  const canBulkCreate = canMutate && !!config.supportsBulk && hasPermission(`${config.permissionPrefix}.Create`);
  const canViewRows = true;
  const canManageRows = canDelete || canUpdate || canViewRows;

  const query = useQuery({
    queryKey: ["entry-page", config.key, activeListUrl, filters],
    enabled: !isDraftView,
    queryFn: () => postPaged<T>(activeListUrl, filters)
  });

  const openCreate = () => {
    setEditId(null);
    setUpsertOpen(true);
  };

  const openEdit = (row: DailySummaryRow) => {
    const id = getNumberValue(row as Record<string, unknown>, ["id", "Id", "representativeId", "RepresentativeId"]);
    if (!id) {
      message.warning("This row cannot be edited from this view.");
      return;
    }
    setEditId(id);
    setUpsertOpen(true);
  };

  const deleteRow = (row: DailySummaryRow) => {
    const id = getNumberValue(row as Record<string, unknown>, ["id", "Id"]);
    if (!id || !config.deleteUrl) {
      message.warning("This row cannot be deleted from this view.");
      return;
    }

    confirmDeletePreview({
      entityName: config.title,
      actionDescription: config.key === "DailyEntry"
        ? "This will reverse the accounting posting and move the entry to Deleted Entries."
        : undefined,
      okText: config.key === "DailyEntry" ? "Reverse & Delete" : undefined,
      fields: [
        { label: "Date", value: getStringValue(row as Record<string, unknown>, ["entryDate", "EntryDate", "paymentEntryDate", "PaymentEntryDate"]), type: "date" },
        { label: "Paid Of", value: getStringValue(row as Record<string, unknown>, ["paidOfName", "PaidOfName", "partyName", "PartyName"]) },
        { label: "Category", value: getStringValue(row as Record<string, unknown>, ["entryCategoryName", "EntryCategoryName", "ledgerName", "LedgerName"]) },
        { label: "Payment", value: getStringValue(row as Record<string, unknown>, ["paymentType", "PaymentType", "paymentTypeName", "PaymentTypeName"]), type: "tag" },
        { label: "Status", value: getStringValue(row as Record<string, unknown>, ["paymentStatus", "PaymentStatus"]), type: "tag" },
        { label: "Amount", value: getNumberValue(row as Record<string, unknown>, ["amount", "Amount", "paidAmount", "PaidAmount"]), type: "money" }
      ],
      onConfirm: async () => {
        try {
          await deleteEntry(config.deleteUrl!, id);
          message.success(config.key === "DailyEntry" ? "Daily entry reversed and moved to Deleted Entries" : `${config.title} deleted`);
          setDetailsRows((current) => current.filter((item) => getNumberValue(item, ["id", "Id"]) !== id));
          await query.refetch();
        } catch (err) {
          message.error(getApiErrorMessage(err, `Unable to delete ${config.title}`));
        }
      }
    });
  };

  const deleteSelectedRows = () => {
    if (!canDelete || config.key !== "DailyEntry" || !config.deleteUrl || !selectedEntryIds.length) return;
    const count = selectedEntryIds.length;
    Modal.confirm({
      title: count === 1 ? "Delete selected daily entry?" : `Delete ${count} selected daily entries?`,
      content: "Each selected entry posting will be reversed and moved to Deleted Entries. Nothing will be physically removed.",
      okText: "Reverse & Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const deleted = await bulkDeleteEntry(config.deleteUrl!, selectedEntryIds);
          setSelectedEntryIds([]);
          message.success(`${deleted} daily entr${deleted === 1 ? "y" : "ies"} reversed and moved to Deleted Entries`);
          await query.refetch();
        } catch (err) {
          message.error(getApiErrorMessage(err, "Unable to delete selected daily entries"));
        }
      }
    });
  };

  const permanentlyDeleteDailyRow = (row: DailySummaryRow) => {
    const id = getNumberValue(row as Record<string, unknown>, ["id", "Id"]);
    if (!id || !canDelete) return;
    Modal.confirm({
      title: "Permanently delete this daily entry?",
      content: "This physically removes the deleted source entry and its balanced journal pair. This cannot be undone.",
      okText: "Permanently Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await permanentlyDeleteEntry("/daily-entries/deleted", id);
          message.success("Daily entry permanently deleted");
          await query.refetch();
        } catch (err) {
          message.error(getApiErrorMessage(err, "Unable to permanently delete daily entry"));
        }
      }
    });
  };

  const openDeletedDailyDetails = (row: DailySummaryRow) => {
    setDetailsTitle("Deleted Daily Entry");
    setDetailsRows([row]);
    setDetailsRecord(row as Record<string, unknown>);
    setDetailsError(null);
    setDetailsLoading(false);
    setDetailsGroup(null);
    setDetailsOpen(true);
  };

  const openPairDetails = async (row: DailySummaryRow) => {
    if (config.key !== "DailyEntry") {
      await openEntryDetails(row);
      return;
    }
    const raw = row as Record<string, unknown>;
    const paidOfId = getNumberValue(raw, ["paidOfId", "PaidOfId", "partyId", "PartyId"]);
    const paymentOfId = getNumberValue(raw, ["paymentOfId", "PaymentOfId"]) ?? 0;
    const entryDate = getStringValue(raw, ["entryDate", "EntryDate", "paymentEntryDate", "PaymentEntryDate"]);

    if (!paidOfId) {
      message.warning("This summary row does not include a Paid Of key.");
      return;
    }
    setDetailsTitle(`${getStringValue(raw, ["paidOfName", "PaidOfName"]) ?? "Daily Entry"}${entryDate ? ` - ${formatDate(entryDate)}` : ""}`);
    setDetailsGroup({ paidOfId, paymentOfId, entryDate, paidOfName: getStringValue(raw, ["paidOfName", "PaidOfName"]) ?? undefined });
    setDetailsRows([]);
    setDetailsError(null);
    setDetailsLoading(true);
    setDetailsOpen(true);
    try {
      const rows = await getDailyPairDetails<any>({ paidOfId, paymentOfId, entryDate });
      setDetailsRows(rows);
    } catch {
      setDetailsError("Unable to load entry details.");
      message.error("Unable to load entry details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const openEntryDetails = async (row: DailySummaryRow) => {
    const raw = row as Record<string, unknown>;
    const id = getNumberValue(raw, ["id", "Id"]);
    setDetailsRows([]);
    setDetailsRecord(raw);
    setDetailsError(null);
    setDetailsLoading(false);
    setDetailsTitle(`${config.title} Details`);
    setDetailsOpen(true);

    if (!id || !config.detailUrl || isStatementBank) return;

    setDetailsLoading(true);
    try {
      const record = await getEntry<Record<string, unknown>>(config.detailUrl, id);
      setDetailsRecord(record ?? raw);
    } catch {
      setDetailsError(`Unable to load ${config.title.toLowerCase()} details.`);
    } finally {
      setDetailsLoading(false);
    }
  };

  const markDailyEntriesPaid = async (row?: DailySummaryRow | Record<string, unknown>) => {
    const raw = (row ?? detailsGroup ?? {}) as Record<string, unknown>;
    const paidOfId = getNumberValue(raw, ["paidOfId", "PaidOfId"]);
    const paymentOfId = getNumberValue(raw, ["paymentOfId", "PaymentOfId"]) ?? 0;
    const entryDate = getStringValue(raw, ["entryDate", "EntryDate", "paymentEntryDate", "PaymentEntryDate"]);
    if (!paidOfId || !entryDate) {
      message.warning("The selected group does not have enough information to mark it as paid.");
      return;
    }

    try {
      const affected = await markDailyGroupPaid({ paidOfId, paymentOfId, entryDate });
      message.success(`${affected} entr${affected === 1 ? "y" : "ies"} marked as paid.`);
      await Promise.all([query.refetch(), detailsOpen ? openPairDetails(raw as DailySummaryRow) : Promise.resolve()]);
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to update payment status."));
    }
  };

  const printDailyGroup = () => {
    if (!detailsRows.length) {
      message.warning("No daily entry details available to print.");
      return;
    }
    const popup = window.open("", "_blank", "noopener,noreferrer");
    if (!popup) {
      message.warning("Popup blocked. Please allow popups to print this view.");
      return;
    }

    const rowsHtml = detailsRows.map((row, index) => {
      const status = row.paymentStatus === 2 || row.paymentStatus === "Paid" ? "Paid" : row.paymentStatus === 3 || row.paymentStatus === "Failed" ? "Failed" : "Pending";
      const paymentType = row.paymentTypeName ?? row.paymentType ?? "-";
      const transactionType = row.transactionTypeName ?? row.transactionType ?? "-";
      return `
        <section class="card">
          <div class="card-head">
            <div>
              <div class="amount">${formatInr(Number(row.amount ?? 0))}</div>
              <div class="meta">${formatDate(row.paymentEntryDate)} | ${row.paidOfName ?? "-"} | ${row.shortRemarkName ?? row.longRemarks ?? "-"}</div>
            </div>
            <div class="pill-row">
              <span class="pill">${status}</span>
              <span class="pill">${paymentType}</span>
              <span class="pill">${transactionType}</span>
            </div>
          </div>
          <table>
            <tr><th>Payment Of</th><td>${row.paymentOfName ?? "-"}</td><th>Bank</th><td>${row.bankName ?? "-"}</td></tr>
            <tr><th>Bank A/C</th><td>${row.bankAccountNumber ?? "-"}</td><th>IFSC</th><td>${row.ifscCode ?? "-"}</td></tr>
            <tr><th>Reference</th><td>${row.paymentReference ?? "-"}</td><th>Payment Mode</th><td>${row.paymentMode ?? "-"}</td></tr>
            <tr><th>Paid Date</th><td>${formatDate(row.paymentPaidDate)}</td><th>Expense Month</th><td>${row.expenseMonthPayment ?? "-"}</td></tr>
            <tr><th>Long Remarks</th><td colspan="3">${row.longRemarks ?? "-"}</td></tr>
          </table>
          <div class="footer">Entry ${index + 1} of ${detailsRows.length} | Entered By ${row.enteredByName ?? row.enteredBy ?? "-"}</div>
        </section>
      `;
    }).join("");

    popup.document.write(`
      <html>
        <head>
          <title>${detailsTitle}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #172033; }
            h1 { margin: 0 0 6px; font-size: 24px; }
            .sub { margin-bottom: 18px; color: #5b6b82; }
            .summary { margin-bottom: 18px; font-weight: 700; }
            .card { border: 1px solid #d7dfeb; border-radius: 10px; padding: 14px; margin-bottom: 14px; break-inside: avoid; }
            .card-head { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
            .amount { font-size: 22px; font-weight: 700; color: #0f4c81; }
            .meta { margin-top: 4px; color: #5b6b82; font-size: 12px; }
            .pill-row { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
            .pill { border: 1px solid #cbd5e1; border-radius: 999px; padding: 4px 10px; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; font-size: 12px; vertical-align: top; }
            th { width: 140px; background: #f8fafc; }
            .footer { margin-top: 10px; color: #64748b; font-size: 11px; }
            @media print { body { margin: 12px; } }
          </style>
        </head>
        <body>
          <h1>${detailsTitle}</h1>
          <div class="sub">Daily Entry Details</div>
          <div class="summary">Total: ${formatInr(detailsRows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0))}</div>
          ${rowsHtml}
          <script>window.onload = function(){ window.print(); };</script>
        </body>
      </html>
    `);
    popup.document.close();
  };

  return (
    <div>
      <PageHeaderActions
        title={config.title}
        subtitle={config.subtitle}
        left={config.key === "DailyEntry" || config.key === "StatementEntry" ? <div className="active-context"><Tag className="view-state-tag">{view}</Tag></div> : null}
        right={
          <>
            <EntryViewToggle configKey={config.key} view={view} setView={setView} resetFilters={() => setFilters({ pageNumber: 1, pageSize: 100 })} />
            <Button icon={<FilterOutlined />} onClick={() => setFiltersOpen(true)}>Filters</Button>
            {config.supportsPendingReport ? <Button onClick={() => setPendingOpen(true)}>Pending Report</Button> : null}
            <Button icon={<FileExcelOutlined />} onClick={() => setImportOpen(true)}>Import</Button>
            {canCreate ? <Button onClick={openCreate}>Create</Button> : null}
            {canBulkCreate && !isDraftView ? <Button type="primary" icon={<PlusCircleOutlined />} onClick={() => { setSelectedDraft(null); setBulkOpen(true); }}>Add</Button> : null}
            {config.key === "DailyEntry" && canDelete && !isDraftView && !isDeletedView ? (
              <Button danger icon={<DeleteOutlined />} disabled={!selectedEntryIds.length} onClick={deleteSelectedRows}>
                Delete ({selectedEntryIds.length})
              </Button>
            ) : null}
            <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} />
          </>
        }
      />
      {filterTags.length ? (
        <div className="active-filter-tags">
          {filterTags.map((tag) => (
            <Tag
              key={tag.key}
              closable
              className="filter-chip"
              onClose={(event) => {
                event.preventDefault();
                const next = { ...filters };
                delete next[tag.key];
                setFilters(next);
              }}
            >
              {tag.label}: {tag.value}
            </Tag>
          ))}
          <Button size="small" type="link" onClick={() => setFilters({ pageNumber: 1, pageSize: 100 })}>Clear filters</Button>
        </div>
      ) : null}
      {isDraftView ? (
        <EntryDraftList
          moduleKey={config.key as "DailyEntry" | "CashEntry"}
          active={isDraftView}
          onOpen={(draft) => {
            setSelectedDraft(draft);
            setBulkOpen(true);
          }}
        />
      ) : (
        <>
          <DataGrid<T>
            rows={(query.data?.items ?? []) as T[]}
            columns={isDeletedView ? [
              ...(activeColumns as ColumnsType<T>),
              {
                key: "actions",
                title: "Actions",
                width: 190,
                fixed: "right" as const,
                render: (_: unknown, row: T) => canDelete ? (
                  <Button danger size="small" icon={<DeleteOutlined />} onClick={(event) => { event.stopPropagation(); permanentlyDeleteDailyRow(row); }}>
                    Delete Permanently
                  </Button>
                ) : null
              }
            ] : [
              ...(activeColumns as ColumnsType<T>),
              ...(canManageRows ? [buildActionColumn<T>({
                dailyEntry: config.key === "DailyEntry",
                canViewRows,
                canUpdate,
                canDelete,
                openRowDetails: config.key === "DailyEntry" ? (row) => openPairDetails(row) : openEntryDetails,
                openEdit,
                deleteRow
              })] : [])
            ]}
            loading={query.isLoading}
            tableId={`${config.key}-${view}`}
            pagination={{
              current: Number(filters.pageNumber ?? 1),
              pageSize: Number(filters.pageSize ?? 100),
              total: query.data?.total ?? 0,
              showSizeChanger: true,
              onChange: (pageNumber, pageSize) => setFilters((current) => ({ ...current, pageNumber, pageSize }))
            }}
            rowSelection={config.key === "DailyEntry" && !isDraftView && !isDeletedView ? {
              selectedRowKeys: selectedEntryIds.map(String),
              getCheckboxProps: (row) => ({ disabled: !getNumberValue(row as Record<string, unknown>, ["id", "Id"]) }),
              onChange: (_, selectedRows) => setSelectedEntryIds(selectedRows
                .map((row) => getNumberValue(row as Record<string, unknown>, ["id", "Id"]))
                .filter((id): id is number => typeof id === "number"))
            } : undefined}
            onRowDoubleClick={(row) => isDeletedView ? openDeletedDailyDetails(row) : openPairDetails(row)}
          />
          {query.error ? <div className="inline-error">Unable to load {config.title}. Check API/auth/tenant context.</div> : null}
        </>
      )}
      <FilterDrawer
        open={filtersOpen}
        moduleKey={config.key}
        values={filters}
        onClose={() => setFiltersOpen(false)}
        onReset={() => setFilters({ pageNumber: 1, pageSize: 100 })}
        onApply={(values) => setFilters({ ...filters, ...normalizeFilterValues(values), pageNumber: 1 })}
      />
      <ExcelImportModal open={importOpen} module={config.importModule ?? "daily-entry"} onClose={() => setImportOpen(false)} onImported={() => query.refetch()} />
      <BulkEntryModal
        open={bulkOpen}
        moduleKey={config.key as "DailyEntry" | "CashEntry" | "StatementEntry"}
        draft={selectedDraft}
        onClose={() => { setBulkOpen(false); setSelectedDraft(null); }}
        onSaved={() => query.refetch()}
        onDraftSaved={() => undefined}
      />
      <EntryUpsertDrawer open={upsertOpen} config={config} editId={editId} onClose={() => setUpsertOpen(false)} onSaved={() => query.refetch()} />
      <PendingReportModal open={pendingOpen} onClose={() => setPendingOpen(false)} filters={filters} />
      <Drawer title={detailsTitle} width="min(1440px, calc(100vw - 16px))" className="details-offcanvas" open={detailsOpen} onClose={() => setDetailsOpen(false)}>
        {config.key === "DailyEntry" ? (
          <DailyEntryDetailsCanvas
            rows={detailsRows}
            loading={detailsLoading}
            error={detailsError}
            canEdit={!isDeletedView && canUpdate}
            canDelete={!isDeletedView && canDelete}
            canMarkPaid={!isDeletedView && hasPermission(`${config.permissionPrefix}.Update`)}
            onEditRow={(row) => openEdit(row)}
            onDeleteRow={(row) => deleteRow(row)}
            onMarkGroupPaid={() => markDailyEntriesPaid()}
            onPrintGroup={printDailyGroup}
          />
        ) : (
          <EntryDetailsCanvas moduleKey={config.key} record={detailsRecord} loading={detailsLoading} error={detailsError} />
        )}
      </Drawer>
    </div>
  );
}

function EntryViewToggle({
  configKey,
  view,
  setView,
  resetFilters
}: {
  configKey: string;
  view: string;
  setView: (view: string) => void;
  resetFilters: () => void;
}) {
  if (configKey === "DailyEntry") {
    return <Segmented className="entry-view-toggle" value={view} onChange={(v) => setView(String(v))} options={[{ label: "Summary", value: "summary" }, { label: "Details", value: "details" }, { label: "Drafts", value: "drafts" }, { label: "Deleted", value: "deleted" }]} />;
  }
  if (configKey === "CashEntry") {
    return <Segmented className="entry-view-toggle" value={view} onChange={(v) => setView(String(v))} options={[{ label: "Entries", value: "summary" }, { label: "Drafts", value: "drafts" }]} />;
  }
  if (configKey !== "StatementEntry") return null;
  return (
    <Segmented
      className="entry-view-toggle"
      value={view}
      onChange={(v) => {
        setView(String(v));
        resetFilters();
      }}
      options={[{ label: "Bank Statement", value: "bank" }, { label: "Manual Entries", value: "manual" }]}
    />
  );
}

function buildActionColumn<T extends DailySummaryRow>({
  dailyEntry,
  canViewRows,
  canUpdate,
  canDelete,
  openRowDetails,
  openEdit,
  deleteRow
}: {
  dailyEntry: boolean;
  canViewRows: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  openRowDetails: (row: DailySummaryRow) => void;
  openEdit: (row: DailySummaryRow) => void;
  deleteRow: (row: DailySummaryRow) => void;
}): ColumnsType<T>[number] {
  return {
    key: "actions",
    title: "",
    width: dailyEntry ? 156 : (canUpdate && canDelete ? 126 : 92),
    fixed: "right" as const,
    render: (_: unknown, row: T) => (
      <Space size={4}>
        {canViewRows ? (
          <Button size="small" title="View details" icon={<EyeOutlined />} onClick={(event) => { event.stopPropagation(); openRowDetails(row); }} />
        ) : null}
        {!dailyEntry && canUpdate && getNumberValue(row as Record<string, unknown>, ["id", "Id", "representativeId", "RepresentativeId"]) ? (
          <Button size="small" title="Edit" icon={<EditOutlined />} onClick={(event) => { event.stopPropagation(); openEdit(row); }} />
        ) : null}
        {canDelete && getNumberValue(row as Record<string, unknown>, ["id", "Id"]) ? (
          <Button danger size="small" icon={<DeleteOutlined />} onClick={(event) => { event.stopPropagation(); deleteRow(row); }} />
        ) : null}
      </Space>
    )
  };
}
